import { useCallback, useEffect, useRef, useState } from "react";
import { getEmissionFactors } from "../kpiGridApi";
import { factorsByKpiFromWire } from "./helpers";

/**
 * Every GHG factor for the KPIs currently loaded, keyed by KPI id.
 *
 * This is the emission relationship that actually exists in the data:
 * `zb_reporting_questions.emission_category_id` and `emission_mapping` are
 * both NULL on every row, while `riu_ghg_emission_factors.kpi_id` carries the
 * scope, the country, the unit, the factor and the database it came from. A
 * KPI routinely has several — 6697 has three across two databases — so the
 * whole list is returned rather than a winner.
 *
 * Order is the endpoint's, which sorts by database priority, so the first
 * factor for a unit is the one that would be applied.
 *
 * Failure is reported, never read as "no factors": the save replaces each
 * KPI's whole factor list, so editing on top of a failed load would delete
 * every stored factor of that KPI. `error` lets the grid lock the factor cells
 * (the rest of the row stays editable) and offer a retry.
 *
 * @param {(number|string)[]} kpiIds
 */
export function useEmissionFactors(kpiIds) {
  const [factorsByKpi, setFactorsByKpi] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const fetchIdRef = useRef(0);
  const key = kpiIds.join(",");

  const load = useCallback(async () => {
    if (!key) {
      setFactorsByKpi({});
      return;
    }
    const fetchId = ++fetchIdRef.current;
    setLoading(true);
    setError(false);
    try {
      const res = await getEmissionFactors(key.split(","));
      if (fetchId !== fetchIdRef.current) return;
      const rows = res?.data?.data;
      if (res?.isSuccess === false || !Array.isArray(rows)) {
        setFactorsByKpi({});
        setError(true);
        return;
      }
      setFactorsByKpi(factorsByKpiFromWire(rows));
    } catch (e) {
      if (fetchId === fetchIdRef.current) {
        setFactorsByKpi({});
        setError(true);
      }
    } finally {
      if (fetchId === fetchIdRef.current) setLoading(false);
    }
  }, [key]);

  useEffect(() => {
    load();
  }, [load]);

  return { factorsByKpi, loading, error, refresh: load };
}
